if (!customElements.get('fixed-height')) {
  /**
   * Container for fixed height elements to ensure matching heights.
   *
   * @class FixedHeightSet
   * @extends {ElementBase}
   */
  class FixedHeightSet extends ElementBase {
    constructor() {
      super();

      this.setID = this.getAttribute('set') ?? null;
      this.childElements = Array.from(
        this.querySelectorAll(`fixed-height[set="${this.setID}"]`)
      );

      this.addResizeListener(window, this.resize);
      this.onBreakpointToggle = this.handleBreakpointToggle.bind(this);
    }

    /**
     * Returns the default element options.
     * @property {string} options.set - The ID of the set.
     * @property {string} [options.breakpoint] - Optional breakpoint to enable fixed height functionality. Defaults to `min-width` query, can be used as `max-width` query by prefixing value with "<".
     *
     * @returns {{set: string, breakpoint: string}}
     */
    defaults() {
      return {
        set: null,
        breakpoint: null,
      };
    }

    /**
     * Initializes the element.
     *
     * Adds matchMedia change listener if `breakpoint` option is set.
     */
    init() {
      super.init();

      this.breakpoint = this.getBreakpoint();
      this.onBreakpointToggle();
      this.breakpoint?.addEventListener('change', this.onBreakpointToggle);

      this.resize();
    }

    /**
     * Destroys the element.
     *
     * Clears element height for all members of the set and removes matchMedia change listener if `breakpoint` option is set.
     */
    destroy() {
      super.destroy();

      this.clearChildren();
      this.breakpoint?.removeEventListener('change', this.onBreakpointToggle);
    }

    /**
     * Clears the element height of all members of the set.
     */
    clearChildren() {
      for (const child of this.childElements) {
        if (!child.initialized) continue;

        child.clearHeight();
      }
    }

    /**
     * Returns the maximum element height of all members of the set.
     *
     * @returns {number}
     */
    getHeight() {
      return this.childElements.reduce(
        (height, element) =>
          Math.max(height, FixedHeightElement.getElementHeight(element)),
        0
      );
    }

    /**
     * Sets the element height of all members of the set based on the maximum height of said elements.
     */
    setHeight() {
      const height = this.getHeight();
      for (const child of this.childElements) {
        child.setHeight(height);
      }
    }

    /**
     * Callback for breakpoint change event. Triggers element height updates.
     */
    resize() {
      if (!this.initialized) return;

      setTimeout(() => {
        this.clearChildren();

        setTimeout(this.setHeight.bind(this));
      }, 100);
    }

    /**
     * Returns the breakpoint media query that determines whether fixed height functionality should be active.
     *
     * @returns {MediaQueryList}
     */
    getBreakpoint() {
      if (!this.options.breakpoint) return null;

      const operatorMap = {
        '<': 'max',
        '>': 'min',
      };
      let operator = 'min';
      let breakpoint = this.options.breakpoint;

      if (Object.keys(operatorMap).includes(breakpoint[0])) {
        operator = operatorMap[breakpoint[0]];
        breakpoint = breakpoint.slice(1);
      }

      const queryString = breakpointQuery(breakpoint, operator);

      return window.matchMedia(queryString);
    }

    /**
     * Toggles fixed height functionality upon changes to the breakpoint media query.
     */
    handleBreakpointToggle() {
      const breakpointMatch = this.breakpoint?.matches ?? true;

      if (breakpointMatch && !this.initialized) {
        this.init();
      } else if (!breakpointMatch && this.initialized) {
        this.destroy();
      }
    }
  }

  customElements.define('fixed-height-set', FixedHeightSet);

  /**
   * Element with a fixed height calculated on window resize.
   * - Used as a standalone element to prevent element resizing upon user interaction.
   * - Used as a part of a fixed height set to match heights and prevent element resizing upon user interaction with any element in the set.
   *
   * @class FixedHeightElement
   * @extends {ElementBase}
   */
  class FixedHeightElement extends ElementBase {
    constructor() {
      super();

      this.setID = this.getAttribute('set') ?? null;
      this.set = this.setID
        ? this.closest(`fixed-height-set[set="${this.setID}"]`)
        : null;
      this.parent = (() => {
        const parent = this.closest('fixed-height');

        if (parent && parent !== this) return parent;
      })();

      if (!this.set && !this.parent) {
        this.addResizeListener(window, this.resize);
      }

      if (this.querySelector('accordion-item')) {
        this.setAttribute('min', true);
      }

      this.addAttributeListener(
        'fixed-height',
        this.setElementHeight.bind(this)
      );
      this.onBreakpointToggle = this.handleBreakpointToggle.bind(this);
    }

    /**
     * Returns the default element options.
     * @property {boolean} [options.min=false] - Whether or not to use `min-height` property.
     * @property {string} [options.breakpoint] - Optional breakpoint to enable fixed height functionality. Defaults to `min-width` query, can be used as `max-width` query by prefixing value with "<".
     * @property {number} [options.fixedHeight] - Pixel value used to set fixed height. Height update is triggered on value change.
     *
     * @returns {{min: boolean, breakpoint: (string|null), fixedHeight: (number|null)}}
     */
    defaults() {
      return {
        min: false,
        breakpoint: null,
        fixedHeight: null,
      };
    }

    /**
     * Returns an array of attributes to observe.
     *
     * Observes `fixed-height` attribute to update element height on change.
     *
     * @returns {string[]}
     */
    static get observedAttributes() {
      return ['fixed-height'];
    }

    /**
     * Initializes the element.
     *
     * Adds matchMedia change listener if `breakpoint` option is set.
     */
    init() {
      super.init();

      if (!this.set && !this.parent) {
        this.breakpoint = this.getBreakpoint();
        this.onBreakpointToggle();
        this.breakpoint?.addEventListener('change', this.onBreakpointToggle);

        this.resize();
      }
    }

    /**
     * Destroys the element.
     *
     * Clears element height and removes matchMedia change listener if `breakpoint` option is set.
     */
    destroy() {
      super.destroy();

      this.clearHeight();
      this.breakpoint?.removeEventListener('change', this.onBreakpointToggle);
    }

    /**
     * Callback for breakpoint change event. Triggers element height update.
     */
    resize() {
      if (!this.initialized) return;

      setTimeout(() => {
        this.clearHeight();

        setTimeout(this.setHeight.bind(this));
      }, 100);
    }

    /**
     * Clears the element's height.
     */
    clearHeight() {
      this.removeAttribute('fixed-height');

      FixedHeightElement.clearChildren(this);
    }

    /**
     * Returns the element's current height.
     *
     * @returns {number}
     */
    getHeight() {
      return FixedHeightElement.getElementHeight(this);
    }

    /**
     * Updates the height of any `fixed-height` child elements.
     *
     * @param {Element} element
     */
    static updateChildren(element) {
      const children = element.querySelectorAll('fixed-height');
      if (!children.length) return;

      children.forEach(child => child.setHeight());
    }

    /**
     * Clears the height of any `fixed-height` child elements.
     *
     * @param {Element} element
     */
    static clearChildren(element) {
      const children = element.querySelectorAll('fixed-height');
      if (!children.length) return;

      children.forEach(child => child.clearHeight());
    }

    /**
     * Returns the height of an element, temporarily collapsing any open accordions during the height calculation.
     *
     * @param {Element} element
     * @returns {number}
     */
    static getElementHeight(element) {
      const openAccordions = Array.from(
        element.querySelectorAll('accordion-item[open]')
      );

      // Collapse accordions prior to defining fixed height
      openAccordions.forEach(accordion => accordion.close(true));

      // Update fixed-height children to ensure their height is set before that of their parents
      FixedHeightElement.updateChildren(element);
      const height = element.offsetHeight;

      // Re-open collapsed accordions
      openAccordions.forEach(accordion => accordion.open(false, true));

      return height;
    }

    /**
     * Updates the element's `fixed-height` attribute.
     *
     * @param {number} [height]
     */
    setHeight(height = null) {
      height ??= this.getHeight();
      this.setAttribute('fixed-height', height);
    }

    /**
     * Sets the element's height based on the value of the fixed-height attribute.
     *
     * @param {string} height
     */
    setElementHeight(height) {
      if (!height) {
        gsap.set(this, { clearProps: 'height,minHeight' });
        return;
      }

      if (!this.initialized) return;

      const property = this.options.min ? 'minHeight' : 'height';
      gsap.set(this, { [property]: this.options.fixedHeight });
    }

    /**
     * Returns the breakpoint media query that determines whether fixed height functionality should be active.
     *
     * @returns {MediaQueryList}
     */
    getBreakpoint() {
      if (!this.options.breakpoint) return null;

      const operatorMap = {
        '<': 'max',
        '>': 'min',
      };
      let operator = 'min';
      let breakpoint = this.options.breakpoint;

      if (Object.keys(operatorMap).includes(breakpoint[0])) {
        operator = operatorMap[breakpoint[0]];
        breakpoint = breakpoint.slice(1);
      }

      const queryString = breakpointQuery(breakpoint, operator);

      return window.matchMedia(queryString);
    }

    /**
     * Toggles fixed height functionality upon changes to the breakpoint media query.
     */
    handleBreakpointToggle() {
      const breakpointMatch = this.breakpoint?.matches ?? true;

      if (breakpointMatch && !this.initialized) {
        this.init();
      } else if (!breakpointMatch && this.initialized) {
        this.destroy();
      }
    }
  }

  customElements.define('fixed-height', FixedHeightElement);
}
